/**
 * Mirrors `HwcgActivationLogDto` served by coms `GET
 * /dice/incidents/:id/hwcg-activation-log`.
 */

export type HwcgActivationSource = 'roster' | 'group';

export type HwcgActivationAttempt = {
  channel: string;
  status: string;
  destination: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  respondedAt: string | null;
  responseValue: string | null;
  error: string | null;
};

export type HwcgActivationNoticeEntry = {
  notificationId: string;
  parentNotificationId: string | null;
  step: 1 | 2;
  source: HwcgActivationSource;
  action: 'activate' | 'notify';
  positionId: string | null;
  positionName: string | null;
  groupId: string | null;
  title: string;
  script: string;
  sentAt: string | null;
  attempts: HwcgActivationAttempt[];
  response: string | null;
  responseNote?: string | null;
  respondedAt: string | null;
  acknowledgedAt: string | null;
};

export type HwcgActivationRosterStatus = 'activated' | 'declined' | 'pending';

export type HwcgActivationUserLog = {
  userGuid: string;
  name: string;
  email: string | null;
  isMember: boolean;
  positions: { positionId: string; positionName: string }[];
  status: HwcgActivationRosterStatus | null;
  activatedAt: string | null;
  declinedAt: string | null;
  activationNote: string | null;
  step1Response: string | null;
  step1RespondedAt: string | null;
  step2Response: string | null;
  step2RespondedAt: string | null;
  lastNoticeAt: string | null;
  notices: HwcgActivationNoticeEntry[];
};

export type HwcgActivationEventKind =
  | 'sent'
  | 'delivered'
  | 'failed'
  | 'responded'
  | 'acknowledged'
  | 'activated'
  | 'declined';

export type HwcgActivationEvent = {
  at: string;
  kind: HwcgActivationEventKind;
  userGuid: string;
  name: string;
  step: 1 | 2 | null;
  source: HwcgActivationSource | null;
  channel: string | null;
  positionName: string | null;
  detail: string | null;
  notificationId: string | null;
};

export type HwcgActivationChannelTally = {
  sent: number;
  delivered: number;
  failed: number;
  responded: number;
};

export type HwcgActivationSummary = {
  members: number;
  activated: number;
  declined: number;
  pending: number;
  noticesSent: number;
  usersNoticed: number;
  step1: Record<string, number>;
  step2: Record<string, number>;
  channels: Record<string, HwcgActivationChannelTally>;
  medianMinutesToRespond: number | null;
};

export type HwcgActivationLogDto = {
  incidentId: string;
  generatedAt: string;
  summary: HwcgActivationSummary;
  users: HwcgActivationUserLog[];
  events: HwcgActivationEvent[];
};

// ─── Labels ──────────────────────────────────────────────────────────────────

const CHANNEL_LABELS: Record<string, string> = {
  inApp: 'In-app',
  email: 'Email',
  sms: 'SMS',
  voice: 'Voice',
  teams: 'Teams',
};

export function channelLabel(channel: string | null | undefined): string {
  if (!channel) return '—';
  return CHANNEL_LABELS[channel] ?? channel;
}

export function sourceLabel(
  source: HwcgActivationSource | null | undefined
): string {
  if (source === 'roster') return 'Roster seat';
  if (source === 'group') return 'Group action';
  return '—';
}

export function stepLabel(step: 1 | 2 | null | undefined): string {
  if (step === 1) return 'Step 1 · Confidentiality';
  if (step === 2) return 'Step 2 · Activation notice';
  return '—';
}

export function rosterStatusLabel(
  status: HwcgActivationRosterStatus | null | undefined
): string {
  if (status === 'activated') return 'Activated';
  if (status === 'declined') return 'Declined';
  if (status === 'pending') return 'Pending';
  return 'Not on roster';
}

const ACTIVATED_LABELS = new Set(['EOC activated', 'Reported', 'Activated']);
const DECLINED_LABELS = new Set([
  'EOC cannot be activated',
  'Cannot report',
  'Declined',
]);

/**
 * Deliveries store the option label; EOC and roster step 2 word the same answer
 * differently.
 */
export function normalizeStep2Response(
  label: string | null | undefined
): string {
  const value = (label ?? '').trim();
  if (!value) return 'Awaiting';
  if (ACTIVATED_LABELS.has(value)) return 'Activated';
  if (DECLINED_LABELS.has(value)) return 'Declined';
  return value;
}

/**
 * One answer for a person: the step-2 reply when that notice went out,
 * otherwise step 1.
 */
export function activationNoticeResponse(
  user: HwcgActivationUserLog
): string | null {
  if (user.notices.length === 0) return null;
  if (user.notices.some((notice) => notice.step === 2)) {
    return normalizeStep2Response(user.step2Response);
  }
  return user.step1Response?.trim() || 'Awaiting';
}

export function tallyActivationNoticeResponses(
  users: HwcgActivationUserLog[]
): Record<string, number> {
  const tallies: Record<string, number> = {};
  for (const user of users) {
    const response = activationNoticeResponse(user);
    if (!response) continue;
    tallies[response] = (tallies[response] ?? 0) + 1;
  }
  return tallies;
}

export type GroupNoticeLogRecipient = {
  key: string;
  userGuid: string | null;
  name: string;
  delivery: string;
  acknowledged: string;
  note: string | null;
};

export type GroupNoticeLogRow = {
  notificationId: string;
  sentAt: string | null;
  action: 'activate' | 'notify';
  recipients: GroupNoticeLogRecipient[];
};

export type LatestNoticeContact = {
  id: string;
  userGuid?: string | null;
  name: string;
  notificationId: string | null;
  sentAt: string | null;
  delivery: string;
  acknowledged: string;
  action: 'activate' | 'notify';
};

function noticeDeliveryLabel(attempts: HwcgActivationAttempt[]): string {
  if (
    attempts.some(
      (attempt) =>
        attempt.status === 'failed' ||
        attempt.status === 'bounced' ||
        attempt.status === 'undeliverable'
    )
  ) {
    return 'Failed';
  }
  if (
    attempts.some(
      (attempt) =>
        attempt.status === 'delivered' || attempt.status === 'acknowledged'
    )
  ) {
    return 'Delivered';
  }
  if (attempts.some((attempt) => attempt.status === 'sent')) return 'Sent';
  return '—';
}

function noticeAckLabel(response: string | null): string {
  const raw = (response ?? '').trim();
  if (!raw) return 'Awaiting';
  if (raw === 'EOC activated' || raw === 'Activated' || raw === 'Reported') {
    return 'Activated';
  }
  return raw;
}

function sortNoticeRows(rows: GroupNoticeLogRow[]): GroupNoticeLogRow[] {
  return [...rows].sort((a, b) =>
    (b.sentAt ?? '').localeCompare(a.sentAt ?? '')
  );
}

/**
 * One row per Group Actions notice for the selected group. Roster notices are
 * left out.
 */
export function groupNoticeLogRows(
  users: HwcgActivationUserLog[],
  groupId: string,
  action?: 'activate' | 'notify'
): GroupNoticeLogRow[] {
  const byId = new Map<string, GroupNoticeLogRow>();
  for (const user of users) {
    for (const notice of user.notices) {
      if (notice.source !== 'group' || notice.groupId !== groupId) continue;
      if (action && notice.action !== action) continue;
      const row = byId.get(notice.notificationId) ?? {
        notificationId: notice.notificationId,
        sentAt: notice.sentAt,
        action: notice.action,
        recipients: [],
      };
      if (notice.sentAt && (!row.sentAt || notice.sentAt < row.sentAt)) {
        row.sentAt = notice.sentAt;
      }
      if (
        !row.recipients.some((recipient) => recipient.key === user.userGuid)
      ) {
        row.recipients.push({
          key: user.userGuid,
          userGuid: user.userGuid,
          name: user.name,
          delivery: noticeDeliveryLabel(notice.attempts),
          acknowledged: noticeAckLabel(notice.response),
          note:
            notice.action === 'activate'
              ? notice.responseNote?.trim() || null
              : null,
        });
      }
      byId.set(notice.notificationId, row);
    }
  }
  return sortNoticeRows([...byId.values()]);
}

/** Adds the latest send's contacts who never received a PRATUS delivery row. */
export function mergeLatestNoticeContacts(
  rows: GroupNoticeLogRow[],
  contacts: LatestNoticeContact[]
): GroupNoticeLogRow[] {
  const next = rows.map((row) => ({
    ...row,
    recipients: [...row.recipients],
  }));
  const byId = new Map(next.map((row) => [row.notificationId, row]));
  for (const contact of contacts) {
    if (!contact.notificationId) continue;
    let row = byId.get(contact.notificationId);
    if (!row) {
      row = {
        notificationId: contact.notificationId,
        sentAt: contact.sentAt,
        action: contact.action,
        recipients: [],
      };
      next.push(row);
      byId.set(contact.notificationId, row);
    }
    const key = contact.userGuid || contact.id;
    const already = row.recipients.some(
      (recipient) =>
        recipient.key === key ||
        (contact.userGuid ? recipient.key === contact.userGuid : false)
    );
    if (already) continue;
    row.recipients.push({
      key,
      userGuid: contact.userGuid ?? null,
      name: contact.name,
      delivery: contact.delivery,
      acknowledged: contact.acknowledged,
      note: null,
    });
  }
  return sortNoticeRows(next);
}

/** A recipient can be selected for resend only when they have a PRATUS user id. */
export function hwcgLogRecipientSelectable(recipient: {
  userGuid?: string | null;
}): boolean {
  return Boolean(recipient.userGuid);
}

/**
 * User ids to resend to. Keeps checked ids that belong to this notice and drops
 * anyone who was not a recipient.
 */
export function hwcgNoticeResendUserIds(
  recipients: readonly { userGuid?: string | null }[],
  selected: readonly string[]
): string[] {
  const allowed = new Set(
    recipients
      .map((recipient) => recipient.userGuid)
      .filter((id): id is string => Boolean(id))
  );
  const seen = new Set<string>();
  const userGuids: string[] = [];
  for (const id of selected) {
    if (!allowed.has(id) || seen.has(id)) continue;
    seen.add(id);
    userGuids.push(id);
  }
  return userGuids;
}

export function eventKindLabel(kind: HwcgActivationEventKind): string {
  switch (kind) {
    case 'sent':
      return 'Sent';
    case 'delivered':
      return 'Delivered';
    case 'failed':
      return 'Failed';
    case 'responded':
      return 'Replied';
    case 'acknowledged':
      return 'Acknowledged';
    case 'activated':
      return 'Activated';
    case 'declined':
      return 'Declined';
    default:
      return kind;
  }
}

export function formatLogTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatClock(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Derivations ─────────────────────────────────────────────────────────────

export type HwcgActivationLogFilters = {
  source?: HwcgActivationSource | 'all';
  status?: HwcgActivationRosterStatus | 'all';
  /** Only people who have a notice at this step. */
  step?: 1 | 2 | 'all';
  search?: string;
};

export function filterActivationUsers(
  users: HwcgActivationUserLog[],
  filters: HwcgActivationLogFilters
): HwcgActivationUserLog[] {
  const search = (filters.search ?? '').trim().toLowerCase();
  return users.filter((user) => {
    if (filters.status && filters.status !== 'all') {
      if (user.status !== filters.status) return false;
    }
    if (filters.source && filters.source !== 'all') {
      if (!user.notices.some((notice) => notice.source === filters.source)) {
        return false;
      }
    }
    if (filters.step && filters.step !== 'all') {
      if (!user.notices.some((notice) => notice.step === filters.step)) {
        return false;
      }
    }
    if (search) {
      const haystack = [
        user.name,
        user.email ?? '',
        ...user.positions.map((position) => position.positionName),
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
}

/** Users who hold (or were noticed for) one of the given positions. */
export function usersForPositions(
  users: HwcgActivationUserLog[],
  positionIds: ReadonlySet<string>
): HwcgActivationUserLog[] {
  if (positionIds.size === 0) return [];
  return users.filter(
    (user) =>
      user.positions.some((position) => positionIds.has(position.positionId)) ||
      user.notices.some(
        (notice) => notice.positionId && positionIds.has(notice.positionId)
      )
  );
}

export function usersByGuids(
  users: HwcgActivationUserLog[],
  userGuids: ReadonlySet<string>
): HwcgActivationUserLog[] {
  if (userGuids.size === 0) return [];
  return users.filter((user) => userGuids.has(user.userGuid));
}

/**
 * Summary counts recomputed for a subset of users (position modal scopes to its
 * occupants).
 */
export function summarizeActivationUsers(
  users: HwcgActivationUserLog[]
): Pick<
  HwcgActivationSummary,
  | 'members'
  | 'activated'
  | 'declined'
  | 'pending'
  | 'noticesSent'
  | 'usersNoticed'
  | 'step1'
  | 'step2'
> {
  const out = {
    members: 0,
    activated: 0,
    declined: 0,
    pending: 0,
    noticesSent: 0,
    usersNoticed: 0,
    step1: {} as Record<string, number>,
    step2: {} as Record<string, number>,
  };
  for (const user of users) {
    if (user.isMember) {
      out.members += 1;
      if (user.status === 'activated') out.activated += 1;
      else if (user.status === 'declined') out.declined += 1;
      else out.pending += 1;
    }
    if (user.notices.length === 0) continue;
    out.usersNoticed += 1;
    out.noticesSent += user.notices.length;
    const s1 = user.step1Response?.trim() || 'Awaiting';
    out.step1[s1] = (out.step1[s1] ?? 0) + 1;
    const hasStep2 = user.notices.some((notice) => notice.step === 2);
    const s2 = hasStep2
      ? normalizeStep2Response(user.step2Response)
      : 'Awaiting';
    out.step2[s2] = (out.step2[s2] ?? 0) + 1;
  }
  return out;
}

/**
 * One line for the roster table: where this person is in the two-step flow,
 * independent of the roster status column beside it.
 */
export function latestActivationState(
  user: HwcgActivationUserLog | undefined
): { label: string; at: string | null } | null {
  if (!user || user.notices.length === 0) return null;
  const step2 = [...user.notices].reverse().find((notice) => notice.step === 2);
  if (step2) {
    if (step2.response) {
      return {
        label:
          normalizeStep2Response(step2.response) === 'Activated'
            ? 'Reported'
            : normalizeStep2Response(step2.response) === 'Declined'
              ? 'Cannot report'
              : step2.response,
        at: step2.respondedAt,
      };
    }
    return { label: 'Activation notice sent', at: step2.sentAt };
  }
  const step1 = [...user.notices].reverse().find((notice) => notice.step === 1);
  if (step1?.response) {
    return { label: 'Confidentiality agreed', at: step1.respondedAt };
  }
  const failed = step1?.attempts.every((attempt) =>
    ['failed', 'bounced', 'undeliverable'].includes(attempt.status)
  );
  if (step1 && failed && step1.attempts.length > 0) {
    return { label: 'Notice failed', at: step1.sentAt };
  }
  return { label: 'Notice sent, awaiting', at: step1?.sentAt ?? null };
}

export function indexUsersByGuid(
  users: HwcgActivationUserLog[] | undefined
): Record<string, HwcgActivationUserLog> {
  const out: Record<string, HwcgActivationUserLog> = {};
  for (const user of users ?? []) out[user.userGuid] = user;
  return out;
}
